'use client';

import * as React from 'react';
import clsx from 'clsx';
import styles from './Stepper.module.css';

export interface Step {
  /** Stable identifier for the step. */
  id: string;
  /** Short label shown beside the indicator. */
  label: React.ReactNode;
}

export interface StepperProps {
  steps: Step[];
  /** Zero-based index of the current step. */
  activeStep: number;
  /**
   * When provided, completed steps become clickable so the user can go back.
   * Steps ahead of the active one are never selectable — the caller decides
   * when forward progress is allowed.
   */
  onStepChange?: (index: number) => void;
  className?: string;
}

/**
 * Stepper Component
 *
 * A compact progress indicator for short multi-step flows (2-4 steps).
 *
 * Laws:
 * - Indicator is a 24px circle: number when upcoming/active, check when done
 * - The active step is the only one with full-strength text
 * - Connectors fill as steps complete
 * - Navigation is backwards-only; forward moves are the caller's decision
 */
export function Stepper({ steps, activeStep, onStepChange, className }: StepperProps) {
  return (
    <ol className={clsx(styles.root, className)} aria-label="Progress">
      {steps.map((step, index) => {
        const complete = index < activeStep;
        const active = index === activeStep;
        const interactive = Boolean(onStepChange) && complete;

        const content = (
          <>
            <span className={styles.indicator} aria-hidden="true">
              {complete ? (
                <svg viewBox="0 0 16 16" width="12" height="12" fill="none">
                  <path
                    d="M3.5 8.5l3 3 6-7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                index + 1
              )}
            </span>
            <span className={styles.label}>{step.label}</span>
          </>
        );

        return (
          <li
            key={step.id}
            className={clsx(
              styles.step,
              complete && styles.complete,
              active && styles.active
            )}
            aria-current={active ? 'step' : undefined}
          >
            {interactive ? (
              <button
                type="button"
                className={styles.trigger}
                onClick={() => onStepChange?.(index)}
              >
                {content}
              </button>
            ) : (
              <span className={styles.trigger}>{content}</span>
            )}

            {index < steps.length - 1 && (
              <span className={styles.connector} aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default Stepper;
